// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Accounting period selector component.
 *
 * Displays a dropdown of all available accounting periods and allows selection.
 * Handles fetching periods from the API and managing loading/error states.
 */
import { SavedAccountingPeriodActivityStatus } from '@/accounting/api/types';
import { useAccountingPeriods } from '@/accounting/hooks/use-accounting-periods';
import {
  GeneratePeriodListOptions,
  findDefaultPeriod,
  findMostRecentlyLockedPeriod,
  formatPeriod,
  generatePeriodList,
} from '@/accounting/utilities/generate-period-list';
import { hasLockedActivity } from '@/accounting/utilities/has-locked-activity';
import { toPeriodKey } from '@/accounting/utilities/to-period-key';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { SupportSnackbar } from '@/components/support-snackbar';
import { LockOutlined } from '@/constants/hardfin-standard-icons';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

interface Props {
  /**
   * Callback invoked when a period is selected.
   * Passes the selected accounting period object.
   */
  onPeriodChange: (period: SavedAccountingPeriodActivityStatus) => void;
  /**
   * Optional initial period to select by year and month.
   * If not provided, uses defaultToMostRecentLocked to determine default.
   */
  initialPeriod?: { year: number; month: number };
  /**
   * Optional initial true-up period. When provided, periods will start from this
   * date and end at the current month (no future months).
   * This should be provided after the initial true-up has been created.
   */
  initialTrueUpPeriod?: { year: number; month: number };
  /**
   * If true, defaults to the most recently locked period instead of the
   * next period to close. Useful for review pages where you want to see
   * completed work rather than pending work.
   */
  defaultToMostRecentLocked?: boolean;
}

/**
 * Accounting period selector component.
 * Fetches and displays all available accounting periods in a dropdown.
 */
export function AccountingPeriodSelector(props: Props) {
  const { backendPeriods, isLoading, isFetching, isError } =
    useAccountingPeriods();
  const { user } = useCurrentUser();
  const organizationTimezone = user.organization.timezone;

  // Build options for period list generation
  const periodListOptions: GeneratePeriodListOptions | undefined =
    useMemo(() => {
      if (props.initialTrueUpPeriod) {
        return { initialTrueUpPeriod: props.initialTrueUpPeriod };
      }
      return undefined;
    }, [props.initialTrueUpPeriod]);

  // Generate complete period list with all gaps filled
  const periods = useMemo(() => {
    return generatePeriodList(
      backendPeriods,
      organizationTimezone,
      periodListOptions
    );
  }, [backendPeriods, organizationTimezone, periodListOptions]);

  // Find the "current" period to close (oldest unlocked) - used for chip display
  const currentPeriodToClose = useMemo(() => {
    return findDefaultPeriod(periods);
  }, [periods]);

  // Find default period to select (may differ from currentPeriodToClose based on mode)
  const defaultPeriod = useMemo(() => {
    if (props.initialPeriod) {
      // Find the period matching the initial year/month
      const matchingPeriod = periods.find(
        (p) =>
          p.year === props.initialPeriod?.year &&
          p.month === props.initialPeriod?.month
      );
      if (matchingPeriod) {
        return matchingPeriod;
      }
    }
    // Use most recently locked period if requested, otherwise next period to close
    if (props.defaultToMostRecentLocked) {
      return (
        findMostRecentlyLockedPeriod(periods) ?? findDefaultPeriod(periods)
      );
    }
    return findDefaultPeriod(periods);
  }, [props.initialPeriod, props.defaultToMostRecentLocked, periods]);

  // Track selected period - initialize from initialPeriod if provided to avoid flash
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>(() => {
    if (props.initialPeriod) {
      return toPeriodKey(props.initialPeriod);
    }
    return '';
  });
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  // Track if we've called onPeriodChange for initial selection
  const [hasCalledInitial, setHasCalledInitial] = useState(false);

  // Show snackbar when error occurs
  useEffect(() => {
    if (isError) {
      setShowErrorSnackbar(true);
    }
  }, [isError]);

  // Set initial selection when periods are loaded
  // Wait until the periods query has resolved to avoid selecting a default
  // based on synthetic (all-unlocked) periods during the loading state
  useEffect(() => {
    if (hasCalledInitial) {
      return;
    }

    // Don't lock in a default while the periods query is still loading
    if (isLoading) {
      return;
    }

    // A cached list can predate a lock, so wait for its refetch before choosing.
    if (isFetching) {
      return;
    }

    if (defaultPeriod) {
      const periodKey = toPeriodKey(defaultPeriod);
      setSelectedPeriodKey(periodKey);
      props.onPeriodChange(defaultPeriod);
      setHasCalledInitial(true);
    }
  }, [defaultPeriod, hasCalledInitial, isLoading, isFetching, props]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const periodKey = event.target.value;
    setSelectedPeriodKey(periodKey);
    // A period the user picked during the refetch is never replaced by the default.
    setHasCalledInitial(true);

    // Find the selected period and call the callback
    const [year, month] = periodKey.split('-').map(Number);
    const selectedPeriod = periods.find(
      (p) => p.year === year && p.month === month
    );

    if (selectedPeriod) {
      props.onPeriodChange(selectedPeriod);
    }
  };

  return (
    <>
      <FormControl size="small" sx={{ minWidth: '15rem' }}>
        <InputLabel id="period-select-label">Period</InputLabel>
        <Select
          labelId="period-select-label"
          id="period-select"
          value={selectedPeriodKey}
          label="Period"
          onChange={handlePeriodChange}
          disabled={isLoading || isError}
        >
          {isLoading ? (
            <MenuItem value="">Loading...</MenuItem>
          ) : isError ? (
            <MenuItem value="">Error loading periods</MenuItem>
          ) : periods.length === 0 ? (
            <MenuItem value="">No periods available</MenuItem>
          ) : (
            periods.map((period) => {
              // Show "Current" chip only when NOT in review mode (defaultToMostRecentLocked)
              const showCurrentChip =
                !props.defaultToMostRecentLocked &&
                currentPeriodToClose &&
                period.id === currentPeriodToClose.id;
              const isInitialPeriod =
                props.initialTrueUpPeriod &&
                period.year === props.initialTrueUpPeriod.year &&
                period.month === props.initialTrueUpPeriod.month;

              const isPeriodLocked = hasLockedActivity(period);

              return (
                <MenuItem
                  key={toPeriodKey(period)}
                  value={toPeriodKey(period)}
                  sx={{
                    fontWeight: showCurrentChip ? 'bold' : 'normal',
                    color: showCurrentChip ? 'primary.main' : 'inherit',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    color={isPeriodLocked ? 'text.secondary' : 'inherit'}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>{formatPeriod(period)}</span>
                      {showCurrentChip && (
                        <Chip label="Current" color="primary" size="small" />
                      )}
                      {isInitialPeriod && (
                        <Chip label="Initial" color="default" size="small" />
                      )}
                    </Stack>
                    {isPeriodLocked && (
                      <LockOutlined
                        fontSize="small"
                        sx={{ ml: 1, color: 'text.secondary' }}
                      />
                    )}
                  </Stack>
                </MenuItem>
              );
            })
          )}
        </Select>
      </FormControl>

      <SupportSnackbar
        isOpen={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
        message="An error occurred while loading accounting periods."
      />
    </>
  );
}
